import { ref, unref } from 'vue';
import { queryRows } from '@rdrive/data-duckdb';

/**
 * The project-summary breakdown: categories and their subcategories, each with
 * status counts rolled up by colour, plus the tooltip text, chip icon and the
 * RDrive deep link for each subcategory chip.
 *
 * `apiHost` is expected ALREADY stripped of a leading `api-` (useAppContext does
 * that); it is interpolated straight into the chip links.
 *
 * @param {object} deps
 * @param {object|import('vue').Ref} deps.conn
 * @param {string|import('vue').Ref} deps.apiHost
 * @param {string|import('vue').Ref} deps.projectCode
 */
export function useCategorySummary({ conn, apiHost, projectCode }) {
  // Starts true so the consumer paints its skeleton until the first reload lands
  // Starts NOT loading: nothing fetches until the items filter context first
  // ticks (after the dashboard loads), so until then the list shows its empty
  // state — the pre-refactor behaviour. Starting `true` kept a 400px skeleton
  // on screen through the welcome phase, which made the page taller than the
  // viewport and shifted the (container-anchored) welcome dialog downwards.
  const loading = ref(false);
  const categories = ref({});
  const subcategories = ref({});
  const statusCounts = ref({});

  // Per-query try/catch, matching the component's old local runner: one failing
  // query degrades that section only, it does not abort the whole reload.
  const run = async (query) => {
    try {
      return await queryRows(unref(conn), query);
    } catch (error) {
      console.error('Query execution error:', error, query);
      return [];
    }
  };

  // Get all categories and subcategories from DuckDB. A category is listed when one of its
  // templates is enabled in this project (`current`; always true on an older API, which sends
  // no such column — see `ITEM_TEMPLATES_CURRENT` in data/manifest.js) or still has items in
  // the project — all of `items`, not `filteredItems`, so a disabled category's row does not
  // come and go with the filters
  const getCategoriesAndSubcategories = async () => {
    const categoriesQuery = `
        SELECT DISTINCT
          categoryId AS id,
          categoryTitle AS title,
          c.icon as icon,
          c.sort as sort
        FROM item_templates it
        JOIN categories c ON c.id = it.categoryId
        WHERE it."current"
           OR EXISTS (SELECT 1 FROM items i WHERE i.profileId = it.id)
        ORDER By sort
      `;

    const subcategoriesQuery = `
        SELECT DISTINCT
          subcategoryId AS id,
          subcategoryTitle AS title,
          categoryId as categoryId,
          type as type
        FROM item_templates
      `;

    const [categoriesResult, subcategoriesResult] = await Promise.all([
      run(categoriesQuery),
      run(subcategoriesQuery)
    ]);

    // Convert categories array to object with ID as key
    const categoriesMap = {};
    categoriesResult.forEach(category => {
      categoriesMap[category.id] = category;
    });

    // Convert subcategories array to object with ID as key
    const subcategoriesMap = {};
    subcategoriesResult.forEach(subcategory => {
      subcategoriesMap[subcategory.id] = subcategory;
    });

    return { categories: categoriesMap, subcategories: subcategoriesMap };
  };

  // Get category status counts with tooltips and icons
  const getCategoryStatusCounts = async () => {
    const query = `
        WITH status_counts AS (
          SELECT
            it.categoryId as categoryId,
            s.color AS statusColor,
            s.title AS statusTitle,
            s.sort AS sort,
            s.IsTerminal AS isTerminal,
            COUNT(*) AS value
          FROM filteredItems i
          JOIN item_templates it ON i.profileId = it.id
          JOIN status_templates s ON i.StatusId = s.id
          WHERE it.categoryId IS NOT NULL
          GROUP BY it.categoryId, s.color, s.title, s.sort, s.IsTerminal
        )
        SELECT
          categoryId,
          statusColor,
          statusTitle,
          sort,
          isTerminal,
          value
        FROM status_counts
        ORDER BY categoryId, sort, statusTitle
      `;

    const rawData = await run(query);

    // Process the data to format it as needed by the component
    const result = {};

    rawData.forEach(row => {
      if (!result[row.categoryId]) {
        result[row.categoryId] = { statuses: {}, subcategories: {} };
      }

      if (!result[row.categoryId].statuses[row.statusColor]) {
        result[row.categoryId].statuses[row.statusColor] = {
          count: 0,
          titles: {},
          icon: 'mdi-circle',
          toolTip: '',
          statusColor: row.statusColor,
          sort: row.sort,
          isTerminal: false
        };
      }

      // Add status title and count; the same title can appear in multiple rows
      // (differing sort/isTerminal across templates), so accumulate rather than overwrite
      const value = Number(String(row.value));
      const titles = result[row.categoryId].statuses[row.statusColor].titles;
      titles[row.statusTitle] = (titles[row.statusTitle] || 0) + value;
      result[row.categoryId].statuses[row.statusColor].count += value;
      result[row.categoryId].statuses[row.statusColor].isTerminal =
        result[row.categoryId].statuses[row.statusColor].isTerminal || Boolean(row.isTerminal);
    });

    // Format tooltips and icons
    for (const categoryId in result) {
      for (const color in result[categoryId].statuses) {
        const status = result[categoryId].statuses[color];
        status.toolTip = Object.entries(status.titles)
          .map(([title, count]) => `${title}: ${count}`)
          .join('\n');
        status.icon = Object.keys(status.titles).length > 1 ? 'mdi-circle-multiple' : 'mdi-circle';
      }
    }

    return result;
  };

  // Get subcategory status counts with tooltips, icons, and links
  const getSubcategoryStatusCounts = async () => {
    const query = `
        WITH status_counts AS (
          SELECT
            it.categoryId as categoryId,
            it.subcategoryId as subcategoryId,
            it.type as type,
            s.color AS statusColor,
            s.title AS statusTitle,
            s.sort AS sort,
            s.IsTerminal AS isTerminal,
            COUNT(*) AS value
          FROM filteredItems i
          JOIN item_templates it ON i.profileId = it.id
          JOIN status_templates s ON i.StatusId = s.id
          WHERE it.subcategoryId IS NOT NULL
          GROUP BY it.categoryId, it.subcategoryId, it.type, s.color, s.title, s.sort, s.IsTerminal
        )
        SELECT
          categoryId,
          subcategoryId,
          type,
          statusColor,
          statusTitle,
          sort,
          isTerminal,
          value
        FROM status_counts
        ORDER BY subcategoryId, sort, statusTitle
      `;

    const rawData = await run(query);

    // Process the data to format it as needed by the component
    const result = {};

    rawData.forEach(row => {
      if (!result[row.categoryId]) {
        result[row.categoryId] = { subcategories: {} };
      }

      if (!result[row.categoryId].subcategories[row.subcategoryId]) {
        result[row.categoryId].subcategories[row.subcategoryId] = {
          statuses: {},
          type: row.type
        };
      }

      if (!result[row.categoryId].subcategories[row.subcategoryId].statuses[row.statusColor]) {
        result[row.categoryId].subcategories[row.subcategoryId].statuses[row.statusColor] = {
          count: 0,
          titles: {},
          icon: 'mdi-circle',
          toolTip: '',
          statusColor: row.statusColor,
          sort: row.sort,
          isTerminal: false
        };
      }

      // Add status title and count; accumulate because the same title can span multiple rows
      const value = Number(String(row.value));
      const statusEntry = result[row.categoryId].subcategories[row.subcategoryId].statuses[row.statusColor];
      statusEntry.titles[row.statusTitle] = (statusEntry.titles[row.statusTitle] || 0) + value;
      statusEntry.count += value;
      statusEntry.isTerminal = statusEntry.isTerminal || Boolean(row.isTerminal);
    });

    // Format tooltips, icons, and links
    for (const categoryId in result) {
      for (const subcategoryId in result[categoryId].subcategories) {
        const subcategory = result[categoryId].subcategories[subcategoryId];

        for (const color in subcategory.statuses) {
          const status = subcategory.statuses[color];

          // Create tooltip
          status.toolTip = Object.entries(status.titles)
            .map(([title, count]) => `${title}: ${count}`)
            .join('\n');

          // Set icon based on number of distinct status titles
          status.icon = Object.keys(status.titles).length > 1 ? 'mdi-circle-multiple' : 'mdi-circle';

          // Generate link
          const subcategoryType = subcategory.type === 'IssueTemplate' ? 'issues' : 'processes';
          const statusType = subcategory.type === 'IssueTemplate' ? 'issue' : 'form';
          const baseLink = `https://${unref(apiHost)}/${unref(projectCode)}/${subcategoryType}?subcategory-ids=${subcategoryId}&`;

          const queryParts = Object.keys(status.titles).map(title =>
            `${statusType}-statuses=${encodeURIComponent(title)}`
          );

          status.link = baseLink + queryParts.join('&');
        }
      }
    }

    return result;
  };

  // Main function to load all data
  const reload = async () => {
    loading.value = true;

    try {
      // Get all data in parallel for better performance
      const [categoriesAndSubcategories, categoryCounts, subcategoryCounts] = await Promise.all([
        getCategoriesAndSubcategories(),
        getCategoryStatusCounts(),
        getSubcategoryStatusCounts(),
      ]);

      // Update local data
      categories.value = categoriesAndSubcategories.categories;
      subcategories.value = categoriesAndSubcategories.subcategories;

      // Update status counts
      statusCounts.value = Object.assign({}, categoryCounts);

      // Merge subcategory data with category data
      for (const categoryId in subcategoryCounts) {
        if (statusCounts.value[categoryId]) {
          statusCounts.value[categoryId].subcategories = subcategoryCounts[categoryId].subcategories;
        }
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      loading.value = false;
    }
  };

  return { loading, categories, subcategories, statusCounts, reload };
}
